import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { useNavigate, useParams } from "react-router-dom"
import DashboardCard from "../../../components/DashboardComponents/DashboardCard"
import Pagination from "../../../components/Listing/Pagination"
import SearchInput from "../../../components/Listing/SearchInput"
import FilterDropdown from "../../../components/Listing/FilterDropdown"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import { ICONS } from "../../../constants/icons"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import useAdminPermission from "../../../hooks/useAdminPermission"
import {
  CLAIM_SORT_BY_OPTIONS,
  DEFAULT_CLAIM_SORT_BY,
  DEFAULT_CLAIM_SORT_DIRECTION,
  getRewardMediaTypeLabel,
  isRewardMediaImage,
  mapRewardClaimRow,
  mapRewardDetail,
  mapRewardDetailKpi,
} from "./adminRewardsData"
import AddRewardModal from "./AddRewardModal"

const PAGE_SIZE = 10

const REWARD_STATUS_LABEL = {
  active: "Active",
  draft: "Draft",
  inactive: "Inactive",
}

/**
 * Admin Reward Detail — summary from GET /admin/rewards/:id
 * + claimers from GET /admin/reward-claims?reward_id=
 */
const AdminRewardDetail = ({
  listPath = "/admin/rewards",
  documentTitlePrefix = "Reward",
}) => {
  const navigate = useNavigate()
  const { rewardId } = useParams()
  const { can } = useAdminPermission()
  const canEdit = can("rewards", "edit")

  const [reward, setReward] = useState(null)
  const [isRewardLoading, setIsRewardLoading] = useState(true)
  const [rewardError, setRewardError] = useState(null)

  const [rows, setRows] = useState([])
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sortBy, setSortBy] = useState(DEFAULT_CLAIM_SORT_BY)
  const [sortDirection, setSortDirection] = useState(
    DEFAULT_CLAIM_SORT_DIRECTION,
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [isClaimsLoading, setIsClaimsLoading] = useState(true)
  const [claimsError, setClaimsError] = useState(null)
  const [claimsTotal, setClaimsTotal] = useState(null)

  const [isEditOpen, setIsEditOpen] = useState(false)

  useEffect(() => {
    document.title = `${documentTitlePrefix} Detail | AMOK . EARTH Admin`
  }, [documentTitlePrefix])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, sortBy, sortDirection, rewardId])

  const fetchReward = useCallback(async () => {
    if (!rewardId) return
    setIsRewardLoading(true)
    setRewardError(null)
    try {
      const response = await get(endpoints.getAdminRewardById(rewardId), {
        params: { "with[]": ["creator", "medias", "cities"] },
      })
      if (response?.data?.success) {
        setReward(mapRewardDetail(response.data.result || {}))
      } else {
        const message = toastApiError(response, "Failed to load reward")
        setRewardError(message)
        setReward(null)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load reward")
      setRewardError(message)
      setReward(null)
    } finally {
      setIsRewardLoading(false)
    }
  }, [rewardId])

  useEffect(() => {
    fetchReward()
  }, [fetchReward])

  const fetchClaims = useCallback(async () => {
    if (!rewardId) return
    setIsClaimsLoading(true)
    setClaimsError(null)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_SIZE,
        reward_id: rewardId,
        "with[]": ["user", "reward", "medias"],
        sort_by: sortBy || DEFAULT_CLAIM_SORT_BY,
        sort_direction: sortDirection || DEFAULT_CLAIM_SORT_DIRECTION,
      }
      if (debouncedSearch) params.search = debouncedSearch

      const response = await get(endpoints.getAdminRewardClaims, { params })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapRewardClaimRow)

        setRows(mapped)
        setTotalItems(result.total ?? mapped.length)
        setPageSize(result.per_page || PAGE_SIZE)
        setClaimsTotal(result.total ?? mapped.length)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total ?? mapped.length) / (result.per_page || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load claimers")
        setClaimsError(message)
        setRows([])
        setTotalItems(0)
        setClaimsTotal(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load claimers")
      setClaimsError(message)
      setRows([])
      setTotalItems(0)
      setClaimsTotal(0)
    } finally {
      setIsClaimsLoading(false)
    }
  }, [currentPage, debouncedSearch, rewardId, sortBy, sortDirection])

  useEffect(() => {
    fetchClaims()
  }, [fetchClaims])

  const kpiCards = useMemo(
    () => mapRewardDetailKpi(reward || {}, claimsTotal),
    [claimsTotal, reward],
  )

  const skeletonRows = useMemo(
    () => Array.from({ length: 6 }, (_, index) => `skeleton-${index}`),
    [],
  )

  const safePage = Math.min(
    currentPage,
    Math.max(1, Math.ceil(totalItems / pageSize) || 1),
  )

  const handleEditSuccess = async () => {
    await Promise.all([fetchReward(), fetchClaims()])
  }

  if (!isRewardLoading && !reward) {
    return (
      <div className="admin-page admin-rewards admin-reward-detail">
        <EmptyState
          title="Reward not found"
          description={
            rewardError ||
            "This reward may have been removed or the link is invalid."
          }
          actionText="Try again"
          onActionClick={fetchReward}
        />
        <CustomButton
          variant="secondary"
          size="md"
          type="button"
          onClick={() => navigate(listPath)}
        >
          Back to Treasure
        </CustomButton>
      </div>
    )
  }

  return (
    <div className="admin-page admin-rewards admin-reward-detail">
      <div className="admin-reward-detail__header">
        <div className="admin-reward-detail__identity">
          {isRewardLoading ? (
            <>
              <div className="admin-rewards__skeleton admin-reward-detail__skeleton-thumb" />
              <div className="admin-reward-detail__identity-copy">
                <div className="admin-rewards__skeleton admin-reward-detail__skeleton-title" />
                <div className="admin-rewards__skeleton admin-reward-detail__skeleton-meta" />
              </div>
            </>
          ) : (
            <>
              <span
                className="admin-reward-detail__thumb admin-reward-detail__thumb--initials"
                aria-hidden="true"
              >
                {reward.initials}
              </span>
              <div className="admin-reward-detail__identity-copy">
                <h1 className="admin-reward-detail__title">{reward.name}</h1>
                <div className="admin-reward-detail__meta">
                  <span className="admin-reward-detail__type">
                    {reward.typeLabel}
                  </span>
                  <span
                    className={`admin-rewards__status admin-rewards__status--${reward.status}`}
                  >
                    <span
                      className="admin-rewards__status-dot"
                      aria-hidden="true"
                    />
                    {REWARD_STATUS_LABEL[reward.status] || reward.status}
                  </span>
                  <TruncatedText
                    as="span"
                    className="admin-reward-detail__cities"
                    title={reward.citiesLabel || undefined}
                  >
                    {reward.citiesLabel || "--"}
                  </TruncatedText>
                </div>
                {reward.description ? (
                  <p className="admin-reward-detail__description">
                    {reward.description}
                  </p>
                ) : null}
              </div>
            </>
          )}
        </div>

        <div className="admin-reward-detail__header-actions">
          <CustomButton
            variant="transparent"
            size="md"
            type="button"
            onClick={() => navigate(listPath)}
          >
            Back
          </CustomButton>
          {canEdit ? (
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              trailingIconSrc={ICONS.edit}
              trailingIconAlt=""
              disabled={isRewardLoading || !reward}
              onClick={() => setIsEditOpen(true)}
            >
              Edit Reward
            </CustomButton>
          ) : null}
        </div>
      </div>

      <div className="admin-rewards__kpi-grid">
        {kpiCards.map(card => (
          <DashboardCard
            key={card.key}
            title={card.title}
            tone={card.tone}
            className="admin-rewards__kpi-card"
            fullHeight
          >
            <p className="admin-rewards__kpi-value">
              {isRewardLoading || (card.key === "claims" && isClaimsLoading) ? (
                <span className="admin-rewards__skeleton admin-rewards__skeleton--kpi" />
              ) : (
                card.value
              )}
            </p>
          </DashboardCard>
        ))}
      </div>

      <div className="admin-rewards__toolbar">
        <h2 className="admin-rewards__title">Attachments</h2>
      </div>

      <section
        className="admin-reward-detail__attachments"
        aria-label="Treasure attachments"
        aria-busy={isRewardLoading || undefined}
      >
        {isRewardLoading ? (
          <div className="admin-reward-detail__attachments-grid">
            {Array.from({ length: 2 }, (_, index) => (
              <div
                key={`attachment-skel-${index}`}
                className="admin-reward-detail__attachment admin-reward-detail__attachment--skeleton"
                aria-hidden="true"
              >
                <div className="admin-rewards__skeleton admin-reward-detail__attachment-skel" />
              </div>
            ))}
          </div>
        ) : null}

        {!isRewardLoading && (reward?.medias || []).length > 0 ? (
          <ul className="admin-reward-detail__attachments-grid">
            {(reward.medias || []).map(media => {
              const isImage = isRewardMediaImage(media)
              const href = media.url || media.thumbnailUrl || ""
              const typeLabel = getRewardMediaTypeLabel(media)
              const fileName = media.name || "Attachment"

              if (isImage && href) {
                return (
                  <li key={media.id || href} className="admin-reward-detail__attachment">
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="admin-reward-detail__attachment-link"
                    >
                      <img
                        src={media.thumbnailUrl || href}
                        alt=""
                        className="admin-reward-detail__attachment-image"
                      />
                      <span className="admin-reward-detail__attachment-caption">
                        {fileName}
                      </span>
                    </a>
                  </li>
                )
              }

              return (
                <li
                  key={media.id || fileName}
                  className="admin-reward-detail__attachment admin-reward-detail__attachment--file"
                >
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="admin-reward-detail__attachment-file"
                      download={fileName}
                    >
                      <span className="admin-reward-detail__attachment-type">
                        {typeLabel}
                      </span>
                      <span className="admin-reward-detail__attachment-name">
                        {fileName}
                      </span>
                      <img
                        src={ICONS.adminDocumentDownload}
                        alt=""
                        width={16}
                        height={16}
                        className="admin-reward-detail__attachment-download"
                      />
                    </a>
                  ) : (
                    <div className="admin-reward-detail__attachment-file">
                      <span className="admin-reward-detail__attachment-type">
                        {typeLabel}
                      </span>
                      <span className="admin-reward-detail__attachment-name">
                        {fileName}
                      </span>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        ) : null}

        {!isRewardLoading && (reward?.medias || []).length === 0 ? (
          <p className="admin-reward-detail__attachments-empty">
            No attachments uploaded for this treasure.
          </p>
        ) : null}
      </section>

      <div className="admin-rewards__toolbar">
        <h2 className="admin-rewards__title">Claimers</h2>
        <div className="admin-rewards__toolbar-actions">
          <SearchInput
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search by claimer..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-rewards__search"
          />
          <FilterDropdown
            label="Filter"
            filters={[
              {
                key: "sortBy",
                type: "sortBy",
                label: "Sort By",
                value: sortBy,
                defaultValue: DEFAULT_CLAIM_SORT_BY,
                onChange: next => {
                  setSortBy(next || DEFAULT_CLAIM_SORT_BY)
                },
                options: CLAIM_SORT_BY_OPTIONS,
              },
              {
                key: "sortDirection",
                type: "sortDirection",
                label: "Order",
                value: sortDirection,
                defaultValue: DEFAULT_CLAIM_SORT_DIRECTION,
                onChange: next => {
                  setSortDirection(next || DEFAULT_CLAIM_SORT_DIRECTION)
                },
              },
            ]}
          />
        </div>
      </div>

      <div className="admin-rewards__table-wrapper">
        <div
          className="admin-reward-detail__claims-table"
          role="table"
          aria-busy={isClaimsLoading || undefined}
        >
          <div
            className="admin-reward-detail__claims-row admin-reward-detail__claims-row--head"
            role="row"
          >
            <div role="columnheader">Claimer</div>
            <div role="columnheader">Email</div>
            <div role="columnheader">Level</div>
            <div role="columnheader">XP</div>
            <div role="columnheader">Claimed At</div>
          </div>

          {isClaimsLoading &&
            skeletonRows.map(key => (
              <div
                key={key}
                className="admin-reward-detail__claims-row admin-reward-detail__claims-row--skeleton"
                role="row"
              >
                <div className="admin-rewards__skeleton" />
                <div className="admin-rewards__skeleton" />
                <div className="admin-rewards__skeleton" />
                <div className="admin-rewards__skeleton" />
                <div className="admin-rewards__skeleton" />
              </div>
            ))}

          {!isClaimsLoading &&
            rows.map(row => (
              <div
                key={row.id}
                className="admin-reward-detail__claims-row"
                role="row"
              >
                <div
                  className="admin-rewards__cell admin-rewards__cell--name"
                  role="cell"
                >
                  <div className="admin-rewards__name-wrap">
                    {row.image ? (
                      <img
                        src={row.image}
                        alt=""
                        className="admin-rewards__thumb"
                      />
                    ) : (
                      <span
                        className="admin-rewards__thumb admin-rewards__thumb--initials"
                        aria-hidden="true"
                      >
                        {row.initials}
                      </span>
                    )}
                    <TruncatedText className="admin-rewards__name">
                      {row.name}
                    </TruncatedText>
                  </div>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-rewards__cell"
                  role="cell"
                >
                  {row.email}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-rewards__cell"
                  role="cell"
                >
                  {row.levelLabel}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-rewards__cell"
                  role="cell"
                >
                  {row.xpLabel}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-rewards__cell"
                  role="cell"
                >
                  {row.claimedAtLabel}
                </TruncatedText>
              </div>
            ))}

          {!isClaimsLoading && !claimsError && rows.length === 0 && (
            <div className="admin-rewards__empty">
              <EmptyState
                title="No claimers found"
                description="Claims for this reward will show up here."
              />
            </div>
          )}

          {!isClaimsLoading && claimsError && rows.length === 0 && (
            <div className="admin-rewards__empty">
              <EmptyState
                title="Unable to load claimers"
                description={claimsError}
                actionText="Try again"
                onActionClick={fetchClaims}
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={safePage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Claimers"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-rewards__pagination"
      />

      <AddRewardModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSuccess={handleEditSuccess}
        rewardId={rewardId}
      />
    </div>
  )
}

AdminRewardDetail.propTypes = {
  listPath: PropTypes.string,
  documentTitlePrefix: PropTypes.string,
}

AdminRewardDetail.defaultProps = {
  listPath: "/admin/rewards",
  documentTitlePrefix: "Reward",
}

export default AdminRewardDetail
